import { faSlack } from '@fortawesome/free-brands-svg-icons';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { langs } from '@uiw/codemirror-extensions-langs';
import ReactCodeMirror from '@uiw/react-codemirror';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { getSubjectNameByClassID } from '~/api/courses';
import { getProblemById } from '~/api/problems';
import { clusterFinalSubmissions, getSubmissionClusters } from '~/api/submissions';
import BreadCrumb from '~/components/Breadcrumb';
import Select from '~/components/Select';
import { reFormatedTime } from '~/utils';
import styles from './submission_groups.module.scss';
import Loading from '~/components/Loading';

const SubmissionGroups = () => {
    const { id, problem_id, topic_problems_id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const [problem, setProblem] = useState({
        id: 0,
        title: '',
        level_id: 0,
        level_name: '',
        description: '',
    });
    const [subjectName, setSubjectName] = useState('');
    const [allSubmissions, setAllSubmissions] = useState([]);
    const [groups, setGroups] = useState([]);

    const fetchRelevantData = async () => {
        const res = await getProblemById(problem_id);
        setProblem(res.body);

        const fetchSubjectName = await getSubjectNameByClassID(id);
        setSubjectName(fetchSubjectName.body.subject_name);
    };

    const [isLoading, setIsLoading] = useState(false);
    const fetchGroupSubmissions = async () => {
        try {
            setIsLoading(true);
            const storeSubmissions = await clusterFinalSubmissions(topic_problems_id, id);
            const fetchSubmissions = await getSubmissionClusters(topic_problems_id, problem_id, id, searchParams);
            setAllSubmissions(fetchSubmissions.submissions || []);
            
            const fetchGroups = fetchSubmissions.cluster_names || [];
            if (fetchGroups.length > 0 && fetchGroups.length > groups.length) {
                setGroups(fetchGroups);
            }
            setIsLoading(false);
        } catch (error) {
            console.error(error);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchRelevantData();
    }, [id, problem_id]);

    useEffect(() => {
        fetchGroupSubmissions();
    }, [topic_problems_id, problem_id, id, searchParams]);

    const groupOptions = groups.sort().map((group, groupIndex) => ({ value: group.split(" ")[1], label: group }));
    const handleSelectGroup = (group) => {
        if (group) {
            searchParams.set('group', group.value);
        } else {
            searchParams.delete('group');
        }
        setSearchParams(searchParams.toString());
    }

    const [searchTerm, setSearchTerm] = useState('');
    const handleSearch = (event) => {
        if (event.key === 'Enter') {
            if (searchTerm) {
                searchParams.set('search', encodeURIComponent(searchTerm));
            } else {
                searchParams.delete('search');
            }
        }
        setSearchParams(searchParams.toString());
    }

    const [selectedIndices, setSelectedIndices] = useState(new Set());
    const handleToggleCode = (index) => {
        setSelectedIndices((prevSet) => {
            const newSet = new Set(prevSet);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    const isIndexSelected = (index) => {
        return selectedIndices.has(index);
    };

    return (
        <div className={styles.outer_container}>
            <div className="container">
                <div className={styles.inner_container}>
                    <BreadCrumb
                        items={['Home', 'My courses', subjectName, problem.title]}
                        links={['/', '/courses', `/courses/${id}/course`]}
                    />
                    <div className={styles.header}>
                        <div className={styles.header_icon}>
                            <FontAwesomeIcon icon={faSlack} />
                        </div>
                        <div className={styles.header_content}>
                            <div className={styles.header_subtitle}>Exercise</div>
                            <div className={styles.header_title}>{problem.title}</div>
                        </div>
                    </div>
                    <div className={styles.title_container}>
                        <div className={styles.title}>
                            Submission Groups
                        </div>
                        <Link
                            to={`/courses/${id}/topic_problems/${topic_problems_id}/problems/${problem_id}/overview_submissions`}
                            className={styles.go_back}
                        >
                            <div className={styles.go_back_icon}>
                                <FontAwesomeIcon icon={faAngleLeft} />
                            </div>
                            <div className={styles.go_back_text}>Overview Submissions</div>
                        </Link>
                    </div>
                    <div className={styles.action_container}>
                        <div className={styles.group_container}>
                            <div className={styles.group_choose_text}>Choose a group: </div>
                            <Select options={groupOptions} onSelect={handleSelectGroup} />
                        </div>
                        <div className={styles.search_container}>
                            <div className={styles.search_input}>
                                <input
                                    type="text"
                                    placeholder="Search by name, student code, ..."
                                    className={styles.search_form_control}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={handleSearch}
                                />
                            </div>
                        </div>
                    </div>
                    {!!isLoading && (<Loading />)}
                    {!isLoading && (
                        <div className={styles.table}>
                            <div className={styles.table_header}>
                                <div className='col-1'>No.</div>
                                <div className='col-3'>Full name</div>
                                <div className='col-2'>Student code</div>
                                <div className='col-1'>Status</div>
                                <div className='col-1 text-center'>Score/10</div>
                                <div className='col-2'>Submit time</div>
                                <div className='col-1 text-center'>Group</div>
                                <div className='col-1 text-center'>Code</div>
                            </div>
                            <div className={styles.table_body}>
                                {!allSubmissions.length && (
                                    <div className="text-center py-3">This group does not have any submissions.</div>
                                )}
                                {!!allSubmissions.length && allSubmissions.map((submission, indexOfSubmission) => {
                                    return (
                                        <div
                                            key={indexOfSubmission}
                                            className={indexOfSubmission % 2 ? styles.table_odd_row : styles.table_even_row}
                                        >
                                            <div className={styles.table_row}>
                                                <div className='col-1'>{submission.order}</div>
                                                <div className='col-3'>{submission.name}</div>
                                                <div className='col-2'>{submission.student_code}</div>
                                                <div className='col-1'>{submission.status}</div>
                                                <div className='col-1 text-center fw-bold'>{submission.score}</div>
                                                <div className='col-2'>{reFormatedTime(submission.datetime)}</div>
                                                <div className='col-1 text-center fw-bold'>{submission.group_order}</div>
                                                <div className='col-1 d-flex justify-content-center'>
                                                    <div
                                                        className={`${styles.table_row_action} ${isIndexSelected(indexOfSubmission) ? styles.table_row_collapse : styles.table_row_view}`}
                                                        onClick={() => handleToggleCode(indexOfSubmission)}
                                                    >
                                                        {!isIndexSelected(indexOfSubmission) ? 'View' : 'Collapse'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={`${styles.table_code} ${isIndexSelected(indexOfSubmission) ? styles.visible : ''}`}>
                                                <ReactCodeMirror
                                                    value={submission.code}
                                                    extensions={[langs.cpp()]}
                                                    className={styles.table_code_mirror}
                                                    editable={false}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
}

export default SubmissionGroups;